(function () {
  if (window.InkNfiberCart) return;

  const cartUpdateEventName = 'cart:update';
  const feeItemProductTitles = new Set([
    'Custom Design Upload Fee',
    'Artwork Setup Fee',
    'AI Design Generation Fee',
    'DTF transfer price adjustment product',
    'Artwork Design Charge',
  ]);
  const feeItemHandles = new Set([
    'custom-design-upload-fee',
    'artwork-setup-fee',
    'ai-design-generation-fee',
    'dtf-transfer-price-adjustment-product',
    'artwork-design-charge',
  ]);

  function routesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function route(name, fallback) {
    return window.Theme?.routes?.[name] || `${routesRoot()}${fallback}`;
  }

  function cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index);
  }

  function prepareAddPayload(payload) {
    const sectionIds = cartSectionIds();
    const nextPayload = { ...payload };

    if (sectionIds.length) {
      nextPayload.sections = sectionIds.join(',');
      nextPayload.sections_url = window.location.pathname || '/cart';
    }

    return nextPayload;
  }

  function isFeeCartItem(item) {
    const properties = item?.properties || {};

    return properties._inknfiber_fee_item === 'true'
      || properties['_inknfiber_fee_item'] === 'true'
      || properties._hidden_helper_line === 'true'
      || properties['_hidden_helper_line'] === 'true'
      || properties._custom_design_fee_line === 'true'
      || properties['_custom_design_fee_line'] === 'true'
      || properties._upload_fee_line === 'true'
      || properties['_upload_fee_line'] === 'true'
      || properties._hidden_fee_line === 'true'
      || properties['_hidden_fee_line'] === 'true'
      || Boolean(properties._adjustment_for || properties['_adjustment_for'])
      || Boolean(properties._adjustment_type || properties['_adjustment_type'])
      || feeItemHandles.has(item?.handle)
      || feeItemProductTitles.has(item?.product_title);
  }

  function visibleCartItemCount(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      return isFeeCartItem(item) ? sum : sum + Number(item.quantity || 0);
    }, 0);
  }

  function updateCartBubble(count) {
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', count === 0);
    });
    document.querySelectorAll('.cart-bubble__text-count').forEach((counter) => {
      counter.textContent = count < 100 ? String(count) : '99+';
      counter.classList.toggle('hidden', count === 0);
    });
    document.querySelectorAll('cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
  }

  function dispatchCartUpdate(cart, options = {}) {
    const count = Number.isFinite(options.itemCount) ? Number(options.itemCount) : visibleCartItemCount(cart);

    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (_) {
      // Storage can be unavailable in private browsing modes.
    }

    document.dispatchEvent(
      new CustomEvent(cartUpdateEventName, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: options.sourceId || options.source || 'inknfiber-cart',
          data: {
            itemCount: count,
            source: options.source || 'inknfiber-cart',
            sections: options.sections || {},
          },
        },
      })
    );

    updateCartBubble(count);
  }

  async function parseCartResponse(response, fallbackMessage) {
    const text = await response.text();
    let body = null;

    try {
      body = text ? JSON.parse(text) : null;
    } catch (_) {
      body = null;
    }

    if (!response.ok || body?.status >= 400) {
      const message = body?.description || body?.message || text || fallbackMessage;
      const error = new Error(message);
      error.response = body;
      error.status = response.status || body?.status;
      throw error;
    }

    return body || {};
  }

  async function fetchCart() {
    const response = await fetch(route('cart_url', 'cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    return parseCartResponse(response, 'Unable to refresh the cart.');
  }

  let refreshCartStatePromise = null;

  async function refreshCartState(options = {}) {
    if (refreshCartStatePromise) return refreshCartStatePromise;

    refreshCartStatePromise = fetchCart()
      .then((cart) => {
        dispatchCartUpdate(cart, {
          source: options.source || 'inknfiber-cart-lifecycle',
          sourceId: options.sourceId || 'inknfiber-cart-lifecycle',
        });
        return cart;
      })
      .catch((error) => {
        if (options.silent === false) {
          console.warn('[InkNfiber cart] Unable to refresh cart state.', error);
        }
        return null;
      })
      .finally(() => {
        refreshCartStatePromise = null;
      });

    return refreshCartStatePromise;
  }

  function bindCartLifecycleRefresh() {
    if (window.__InkNfiberCartLifecycleRefreshBound) return;
    window.__InkNfiberCartLifecycleRefreshBound = true;

    let refreshTimer = null;
    const scheduleRefresh = (source) => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshCartState({ source, sourceId: source });
      }, 80);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => scheduleRefresh('inknfiber-cart-domcontentloaded'), { once: true });
    } else {
      scheduleRefresh('inknfiber-cart-domcontentloaded');
    }

    window.addEventListener('pageshow', (event) => {
      scheduleRefresh(event.persisted ? 'inknfiber-cart-pageshow-bfcache' : 'inknfiber-cart-pageshow');
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        scheduleRefresh('inknfiber-cart-visibilitychange');
      }
    });
  }

  async function addJson(payload, options = {}) {
    const response = await fetch(route('cart_add_url', 'cart/add.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(prepareAddPayload(payload)),
    });
    const body = await parseCartResponse(response, options.errorMessage || 'Unable to add item to cart.');

    if (options.refresh === false) {
      return { body, sections: body.sections || null, cart: null };
    }

    const cart = await fetchCart();
    dispatchCartUpdate(cart, {
      source: options.source || 'inknfiber-cart',
      sourceId: options.sourceId,
      sections: body.sections || {},
    });

    return { body, sections: body.sections || null, cart };
  }

  window.InkNfiberCart = {
    addItems(items, options = {}) {
      return addJson({ items }, options);
    },
    addJson,
    dispatchCartUpdate,
    fetchCart,
    prepareAddPayload,
    refreshCartState,
    visibleCartItemCount,
  };

  bindCartLifecycleRefresh();
})();
